// @flow

import * as React from "react";
import { withRouter } from "react-router-dom";
import { Form } from "react-final-form";

import { sanitizeUrl } from "@braintree/sanitize-url";
import Button from "../../components/Button";
import Card from "../../components/Card";
import FieldControl from "../../components/FieldControl";
import { FlashMessageConsumer } from "../../components/FlashMessages";
import FormRow from "../../components/FormRow";
import PageHeader from "../../components/PageHeader";
import PageLayout from "../../components/PageLayout";
import PasswordInput from "../../components/PasswordInput";
import TextInput from "../../components/Input/TextInput";

import { activatePassword } from "../../api/actions";
import { PHONE_MAX_LENGTH } from "../../data/formFields/validationConfig";
import { errors } from "../../utils/errors";
import { isFormSubmitting } from "../../utils/helpers";
import { logError } from "../../utils/loggers";
import * as validators from "../../utils/validators";

const PASSWORD = "password";
const PASSWORD_CONFIRMATION = "password_confirmation";
const PHONE_NUMBER = "phone_number";

type ActivateUserProps = {
  match: {
    params: {
      token: string
    }
  },
  location: {
    search: {
      r: string
    }
  },
  history: {
    push: string => void
  },
  loginPath: string,
  basename: string,
  passwordRegex: RegExp,
  passwordError: string
};

type ActivateUserState = {|
  sent: boolean
|};

class ActivateUser extends React.Component<ActivateUserProps, ActivateUserState> {
  state = { sent: false };

  redirect = () => {
    const { location, history, basename } = this.props;

    if (!location || !history) {
      return;
    }
    const params = new URLSearchParams(location.search);
    let redirectUrl = params.get("r");

    function isWhiteListed(url) {
      try {
        // Check if its a finanzierungsportal URL
        return new URL(url).hostname === "finanzierungsportal.ermoeglicher.de";
      } catch {
        return false;
      }
    }

    if (!redirectUrl || !isWhiteListed(redirectUrl)) {
      return;
    }

    redirectUrl = sanitizeUrl(redirectUrl);

    if (redirectUrl.includes(basename)) {
      const newUrl = new URL(redirectUrl).pathname.replace(basename, "/");
      history.push(newUrl);
    } else {
      window.location = redirectUrl;
    }
  };

  submitForm =
    (onError: string => void) =>
    (values: { password: string, password_confirmation: string, phone_number: string }) => {
      const {
        match: {
          params: { token }
        }
      } = this.props;

      return activatePassword({
        activation_token: token,
        ...values
      })
        .then(() => {
          this.setState({ sent: true });
          this.redirect();
        })
        .catch((err: any) => {
          if (err && err.response && err.response.status) {
            onError("Dieser Account wurde bereits aktiviert.");
          } else {
            logError("Error while activating an user", err);
          }
        });
    };

  render() {
    const { sent } = this.state;
    const { loginPath, passwordRegex, passwordError } = this.props;

    return (
      <PageLayout>
        <PageHeader header="Passwort festlegen" bottomMargin />
        <Card>
          {sent ? (
            <>
              <header>
                Vielen Dank! Ihr Passwort wurde erfolgreich festgelegt. Sie können sich jetzt
                anmelden.
              </header>
              <header>
                <Button asRouterLink href={loginPath}>
                  Jetzt anmelden
                </Button>
              </header>
            </>
          ) : (
            <>
              <header>
                Bitte geben Sie ein Passwort an und bestätigen Sie dieses. Anschliessend können Sie
                sich mit Ihrem neuen Passwort anmelden.
              </header>
              <FlashMessageConsumer>
                {({ addMessage }) => {
                  const onError = msg => addMessage(msg, "failure");

                  return (
                    <Form onSubmit={this.submitForm(onError)}>
                      {({ form }) => (
                        <form onSubmit={() => false}>
                          <FormRow>
                            <FieldControl
                              name={PHONE_NUMBER}
                              validate={validators.combineValidators(
                                validators.required(errors.requiredField()),
                                validators.isPhoneNumber(errors.isPhoneNumberCorrect()),
                                validators.maxLength(
                                  PHONE_MAX_LENGTH,
                                  errors.maxLengthCharacters(PHONE_MAX_LENGTH)
                                )
                              )}
                              caption={() => "Telefonnummer"}
                            >
                              {({ input, meta }) => (
                                <TextInput input={input} meta={meta} placeholder="Telefonnummer" />
                              )}
                            </FieldControl>
                            <FieldControl
                              name={PASSWORD}
                              caption={() => "Passwort"}
                              validate={validators.combineValidators(
                                validators.required(errors.requiredField()),
                                validators.isPassword(passwordRegex, passwordError)
                              )}
                            >
                              {({ input }) => (
                                <PasswordInput
                                  input={input}
                                  placeholder="Geben Sie Ihr Passwort an..."
                                />
                              )}
                            </FieldControl>
                            <FieldControl
                              name={PASSWORD_CONFIRMATION}
                              caption={() => "Passwort bestätigen"}
                              validate={validators.combineValidators(
                                validators.required(errors.requiredField()),
                                validators.equalToField(PASSWORD, errors.passwordMatch())
                              )}
                            >
                              {({ input }) => (
                                <PasswordInput
                                  input={input}
                                  placeholder="Geben Sie erneut Ihr Passwort an..."
                                />
                              )}
                            </FieldControl>
                          </FormRow>
                          <Button
                            data-testid="password-reset-button"
                            isDisabled={!form.getState().valid || isFormSubmitting(form)}
                            onClick={form.submit}
                          >
                            Passwort festlegen
                          </Button>
                        </form>
                      )}
                    </Form>
                  );
                }}
              </FlashMessageConsumer>
            </>
          )}
        </Card>
      </PageLayout>
    );
  }
}

export default withRouter(ActivateUser);
